import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { API_KEY_DAYS, type ApiKey, type NewApiKey } from '@pulsepoint/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Copy, KeyRound, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ApiError, get, post } from '../../api/client';
import { useOrganisations } from '../../api/organisations';
import { ChoiceChip } from '../../components/ChoiceChip';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { RowMenu } from '../../components/RowMenu';
import { StatusLabel } from '../../components/StatusLabel';
import { useToast } from '../../components/Toast';
import { formatAgo, formatDate, formatDateTime } from '../../lib/format';

const keysKey = ['api-keys'] as const;
const useApiKeys = () => useQuery({ queryKey: keysKey, queryFn: () => get<ApiKey[]>('/api-keys') });

function useKeyMutation<A, R>(fn: (a: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: keysKey }) });
}

const DAY_LABELS: Record<number, string> = { 90: '3 months', 180: '6 months', 365: '1 year', 730: '2 years' };

function state(k: ApiKey): { tone: 'success' | 'off' | 'fault'; label: string } {
  if (k.revokedAt) return { tone: 'off', label: 'Revoked' };
  if (new Date(k.expiresAt).getTime() <= Date.now()) return { tone: 'off', label: 'Expired' };
  if (new Date(k.expiresAt).getTime() - Date.now() < 30 * 86_400_000) return { tone: 'fault', label: 'Expires soon' };
  return { tone: 'success', label: 'Active' };
}

type Show = 'all' | 'active' | 'soon' | 'ended';
const SHOW_LABELS: Record<Show, string> = { all: 'All keys', active: 'Active', soon: 'Expires soon', ended: 'Revoked or expired' };
function shows(k: ApiKey, show: Show) {
  const label = state(k).label;
  if (show === 'active') return label === 'Active' || label === 'Expires soon';
  if (show === 'soon') return label === 'Expires soon';
  if (show === 'ended') return label === 'Revoked' || label === 'Expired';
  return true;
}

/** Settings, API keys (admins): every organisation's keys for the read-only customer API, in one place. */
export function ApiKeysTab() {
  const keys = useApiKeys();
  const revoke = useKeyMutation((id: string) => post<ApiKey>(`/api-keys/${id}/revoke`));
  const dialog = useDialog();
  const toast = useToast();
  const [show, setShow] = useState<Show>('active');
  // Kept the same list between renders, for the table.
  const rows = useMemo(() => keys.data?.filter((k) => shows(k, show)), [keys.data, show]);

  const columns: Column<ApiKey>[] = [
    {
      key: 'name',
      header: 'Key',
      sortValue: (k) => k.name.toLowerCase(),
      render: (k) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{k.name}</Typography>
          <Typography variant="body2" color="textSecondary" className="num">{k.prefix}…</Typography>
        </Box>
      ),
    },
    { key: 'organisation', header: 'Organisation', sortValue: (k) => k.organisationName.toLowerCase(), render: (k) => k.organisationName },
    { key: 'state', header: 'Status', render: (k) => <StatusLabel tone={state(k).tone}>{state(k).label}</StatusLabel> },
    {
      key: 'used',
      header: 'Last used',
      hideBelow: 'sm',
      sortValue: (k) => (k.lastUsedAt ? -new Date(k.lastUsedAt).getTime() : null),
      render: (k) =>
        k.lastUsedAt ? (
          <Box title={formatDateTime(k.lastUsedAt)}>
            <Typography>{formatAgo(k.lastUsedAt)}</Typography>
            <Typography variant="body2" color="textSecondary">
              {k.lastUsedIp} · {k.requestCount.toLocaleString('en-AU')} request{k.requestCount === 1 ? '' : 's'}
            </Typography>
          </Box>
        ) : (
          <Typography color="textSecondary">Never</Typography>
        ),
    },
    {
      key: 'expires',
      header: 'Expires',
      hideBelow: 'md',
      sortValue: (k) => new Date(k.expiresAt).getTime(),
      render: (k) => (
        <Box>
          <Typography>{formatDate(k.expiresAt)}</Typography>
          <Typography variant="body2" color="textSecondary">Made {formatDate(k.createdAt)}{k.createdByName ? ` by ${k.createdByName}` : ''}</Typography>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' }, mb: 3 }}>
        <Box>
          <Typography variant="h2">API keys.</Typography>
          <Typography color="textSecondary" sx={{ mt: 0.5 }}>
            Each key lets one organisation's own systems read its chargers, drivers, sessions and reports. Read only.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={20} strokeWidth={1.75} />} onClick={() => void dialog.open(CreateKeyDialog, {})} sx={{ flexShrink: 0 }}>
          Create API key
        </Button>
      </Stack>
      <DataTable
        label="API keys"
        empty={
          <EmptyState title={show === 'all' ? 'No API keys yet' : 'No keys to show'}>
            {show === 'all' ? 'Create one when an organisation wants to connect a system of its own, such as finance or reporting.' : 'Choose All keys to see the rest.'}
          </EmptyState>
        }
        rows={rows}
        columns={columns}
        rowKey={(k) => k.id}
        loading={keys.isPending}
        error={keys.error}
        initialSort={{ key: 'expires', direction: 'asc' }}
        searchText={(k) => `${k.name} ${k.prefix} ${k.organisationName} ${k.lastUsedIp ?? ''} ${k.createdByName ?? ''}`}
        searchPlaceholder="Search by key, organisation or IP"
        pageSize={50}
        toolbar={
          <TextField select size="small" value={show} onChange={(e) => setShow(e.target.value as Show)} sx={{ width: { xs: '100%', md: 200 } }} slotProps={{ htmlInput: { 'aria-label': 'Which keys' } }}>
            {(Object.keys(SHOW_LABELS) as Show[]).map((s) => (
              <MenuItem key={s} value={s}>{SHOW_LABELS[s]}</MenuItem>
            ))}
          </TextField>
        }
        rowActions={(k) =>
          k.revokedAt ? null : (
            <RowMenu
              label={`Actions for ${k.name}`}
              items={[
                {
                  label: 'Revoke',
                  icon: Ban,
                  danger: true,
                  onClick: async () => {
                    const ok = await dialog.confirm({
                      title: `Revoke ${k.name} (${k.organisationName})?`,
                      message: 'It stops working at once, and systems using it can no longer read any data. This cannot be undone: create a new key to reconnect.',
                      confirmLabel: 'Revoke',
                      danger: true,
                    });
                    if (!ok) return;
                    try {
                      await revoke.mutateAsync(k.id);
                      toast(`${k.name} was revoked.`);
                    } catch (e) {
                      toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
                    }
                  },
                },
              ]}
            />
          )
        }
      />
    </Box>
  );
}

/** Choose the organisation, name the key and choose how long it lasts; then it's shown once. */
function CreateKeyDialog({ close }: DialogProps) {
  const orgs = useOrganisations();
  const [organisationId, setOrganisationId] = useState('');
  const [orgError, setOrgError] = useState('');
  const [name, setName] = useState('');
  const [days, setDays] = useState<number>(365);
  const [nameError, setNameError] = useState('');
  const [made, setMade] = useState<NewApiKey | null>(null);
  const create = useKeyMutation((body: { organisationId: string; name: string; days: number }) => post<NewApiKey>('/api-keys', body));
  const toast = useToast();

  const go = async () => {
    if (!organisationId) setOrgError('Choose the organisation');
    if (!name.trim()) setNameError('Say what the key is for');
    if (!organisationId || !name.trim()) return;
    try {
      setMade(await create.mutateAsync({ organisationId, name: name.trim(), days }));
    } catch (e) {
      if (e instanceof ApiError && e.fields.name) setNameError(e.fields.name);
      else toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(made!.key);
      toast('The key was copied.');
    } catch {
      toast("Couldn't copy: select the key and copy it.", 'error');
    }
  };

  if (made) {
    return (
      <Dialog open onClose={() => undefined} fullWidth maxWidth="sm">
        <DialogTitle>Copy the key now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon={<KeyRound size={20} strokeWidth={1.75} />} sx={{ mb: 2.5 }}>
            This is the only time the whole key is shown. Pulsepoint keeps only a scrambled copy, so it can't be shown again.
          </Alert>
          <TextField
            value={made.key}
            fullWidth
            label={made.name}
            onFocus={(e) => e.target.select()}
            slotProps={{ htmlInput: { readOnly: true, className: 'num', spellCheck: false } }}
          />
          <Typography color="textSecondary" sx={{ mt: 2 }}>
            Send it to {made.organisationName} securely (not in the same email as the API documentation). It expires on {formatDate(made.expiresAt)}; they'll be emailed a month before.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Copy size={18} strokeWidth={1.75} />} onClick={() => void copy()}>Copy</Button>
          <Button variant="contained" onClick={() => close()}>Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={() => !create.isPending && close()} fullWidth maxWidth="sm">
      <DialogTitle>Create an API key</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 2.5 }}>
          The key reads one organisation's chargers, drivers, sessions and reports, and nothing else. It can't change anything.
        </Typography>
        <TextField
          select
          label="Organisation"
          value={organisationId}
          onChange={(e) => {
            setOrganisationId(e.target.value);
            setOrgError('');
          }}
          error={!!orgError}
          helperText={orgError || (orgs.isPending ? 'Loading…' : ' ')}
          fullWidth
          autoFocus
          sx={{ mb: 1 }}
        >
          {(orgs.data ?? []).map((o) => (
            <MenuItem key={o.id} value={o.id}>{o.businessName}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="What it's for"
          placeholder="e.g. Finance system"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameError('');
          }}
          error={!!nameError}
          helperText={nameError || 'Shown in the list, and in the expiry email'}
          fullWidth
          slotProps={{ htmlInput: { maxLength: 64 } }}
        />
        <Typography sx={{ fontWeight: 500, mt: 3, mb: 1 }}>Expires after</Typography>
        <Box role="radiogroup" aria-label="Expires after" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {API_KEY_DAYS.map((d) => (
            <ChoiceChip key={d} label={DAY_LABELS[d]!} selected={days === d} onClick={() => setDays(d)} />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()} disabled={create.isPending}>Cancel</Button>
        <Button variant="contained" onClick={() => void go()} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create key'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
